<template>
  <div class="userhome">
    <img class="banner" :src="`/Images/Warhall_userhome_banner.png`" />

    <!-- Header bar -->
    <div class="top-bar">
      <div class="user-greeting">
        <span class="greeting-text">Welcome, <strong>{{ $store.state.username }}</strong></span>
        <span class="pro-badge" :class="isPro ? 'pro-badge--pro' : 'pro-badge--free'">{{ getProStatus }}</span>
      </div>
      <div class="top-bar-actions">
        <button v-show="showUpgradeButton" class="btn btn--upgrade" @click.prevent="redirectToStore">Upgrade to Pro</button>
        <form method="POST" action="/create-portal-session" style="display:inline">
          <button id="manageSub" class="btn btn--secondary" type="submit" disabled>Manage Subscription</button>
        </form>
      </div>
    </div>

    <!-- Main content grid -->
    <div class="home-grid">

      <!-- Store & purchases column -->
      <section class="home-card">
        <h2 class="card-title">🛒 Store</h2>
        <button class="btn btn--primary" @click.prevent="redirectToStore">🛒 Visit Warhall Store</button>
        <button class="btn btn--tool" @click="$router.push({ name: 'redeemcode' })">🎁 Redeem Code</button>
        <button class="btn btn--tool" @click="showPurchasesModal = true">🛍️ Purchase History</button>
      </section>

      <!-- My Account column -->
      <section class="home-card">
        <h2 class="card-title">👤 My Account</h2>
        <button class="btn btn--tool" @click="$router.push({ name: 'changepassword' })">🔑 Change Password</button>
        <button class="btn btn--tool" @click="showGamesModal = true">⚔️ Gaming History</button>
      </section>

      <!-- Tools column -->
      <section class="home-card">
        <h2 class="card-title">🛠️ Warhall Creator</h2>
        <a class="creator-learn-more" @click.prevent="$router.push({ name: 'CreatorLanding' })" href="#">What can I build? →</a>

        <!-- Non-admin: Start Creating -->
        <template v-if="adminGames.length === 0">
          <div class="tool-group">
            <button class="group-toggle" @click="showGame = !showGame">
              🚀 Start Creating &nbsp;<span class="chevron">{{ showGame ? '▲' : '▼' }}</span>
            </button>
            <div v-show="showGame" class="group-buttons">
              <button class="btn btn--tool" @click="$router.push({ name: 'AddNewGame' })">🎮 Add new game</button>
              <button class="btn btn--tool" @click="$router.push({ name: 'JoinGame' })">🔗 Join Game</button>
            </div>
          </div>
        </template>

        <!-- Admin: Edit game data group -->
        <template v-else>
          <div class="tool-group">
            <button class="group-toggle" @click="showGame = !showGame">
              🎮 Data &nbsp;<span class="chevron">{{ showGame ? '▲' : '▼' }}</span>
            </button>
            <div v-show="showGame" class="group-buttons">
              <button class="btn btn--tool" @click="$router.push({ name: 'AddNewGame' })">🎮 Add new game</button>
              <button class="btn btn--tool" @click="$router.push({ name: 'ModifyGame' })">✏️ Edit Game Configuration</button>
              <button class="btn btn--tool" @click="$router.push({ name: 'WDS3DataEditor' })">📝 Edit Game Data</button>
              <button class="btn btn--tool" @click="$router.push({ name: 'JoinGame' })">🔗 Join Game</button>
            </div>
          </div>
        </template>

        <!-- Models group -->
        <div class="tool-group">
          <button class="group-toggle" @click="showModels = !showModels">
            🗿 Models &nbsp;<span class="chevron">{{ showModels ? '▲' : '▼' }}</span>
          </button>
          <div v-show="showModels" class="group-buttons">
            <!-- Unfinished generation recovery -->
            <div v-if="pendingTasks.length > 0" class="pending-tasks-notice">
              <span class="pending-tasks-title">⚠️ Unfinished model(s)</span>
              <div v-for="task in pendingTasks" :key="task.taskId" class="pending-task-row">
                <span class="pending-task-date">{{ formatPendingDate(task.createdAt) }}</span>
                <button class="btn btn--tool btn--resume" @click="resumeTask(task.taskId)">Resume →</button>
              </div>
            </div>
            <button class="btn btn--tool" @click="$router.push({ name: 'ModelCreator' })">📷 Add your own models!</button>
            <button class="btn btn--tool" @click="$router.push({ name: 'catalogue' })">🗂️ Catalogue</button>
          </div>
        </div>
      </section>

    </div>

    <!-- Purchases Modal -->
    <div v-if="showPurchasesModal" class="modal-overlay" @click.self="showPurchasesModal = false">
      <div class="modal-box">
        <div class="modal-header">
          <span>🛍️ Purchase History</span>
          <button class="modal-close" @click="showPurchasesModal = false">✕</button>
        </div>
        <div class="modal-body">
          <purchases />
        </div>
      </div>
    </div>

    <!-- Games Modal -->
    <div v-if="showGamesModal" class="modal-overlay" @click.self="showGamesModal = false">
      <div class="modal-box modal-box--slim">
        <div class="modal-header">
          <span>⚔️ Gaming History</span>
          <button class="modal-close" @click="showGamesModal = false">✕</button>
        </div>
        <div class="modal-body">
          <games />
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Purchases from "@/components/userhome/purchases.vue";
import Games from "@/components/userhome/games.vue";

export default {
  name: "Userhome",

  components: {
    purchases: Purchases,
    games: Games,
  },

  data() {
    return {
      email: null,
      showUpgradeButton: false,
      showData: false,
      showGame: false,
      showModels: false,
      showPurchasesModal: false,
      showGamesModal: false,
      adminGames: [],
      pendingTasks: [],
    };
  },

  mounted() {
    this.fetchStripeUser();
    this.fetchAdminGames();
    this.fetchPendingTasks();
  },

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (vm.$store.state.email == null) {
        const redirectPath = to.query.redirect;
        if (redirectPath) {
          vm.$router.push({ name: "login", query: { redirect: redirectPath } });
        } else {
          vm.$router.push({ name: "login" });
        }
      } else {
        vm.email = vm.$store.state.email;
      }
    });
  },

  computed: {
    isPro() {
      return this.$store.state.proStatus != null;
    },
    getProStatus() {
      const response = this.$store.state.proStatus;
      return response != null ? "Pro until " + response : "Free";
    },
  },

  methods: {
    redirectToStore() {
      this.$router.push({ name: "store" });
    },

    async fetchAdminGames() {
      try {
        const res = await fetch('/GetMyAdminGames/', {
          method: 'GET',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        const data = await res.json();
        if (data.success) {
          this.adminGames = data.games;
        }
      } catch {
        // silently ignore — buttons simply stay hidden
      }
    },

    async fetchPendingTasks() {
      try {
        const res = await fetch(`${this.$config.serverUrl}/api/tripo/pending-tasks`, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (data.error === 0) this.pendingTasks = data.result || [];
      } catch (e) {
        // Non-blocking — silently ignore if server is unreachable
      }
    },

    resumeTask(taskId) {
      this.$router.push({ name: 'ModelCreator', query: { resumeTaskId: taskId } });
    },

    formatPendingDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    },

    async fetchStripeUser() {
      try {
        const response = await fetch(`/GetStripeCustomer/`, {
          method: "GET",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
        });
        const data = await response.json();
        if (data.error == 0 && data.result == "True") {
          this.$nextTick(() => {
            const btn = document.getElementById("manageSub");
            if (btn) btn.disabled = false;
          });
        } else {
          this.showUpgradeButton = true;
        }
      } catch (e) {
        this.showUpgradeButton = true;
      }
    },
  },
};
</script>

<style scoped>
.userhome {
  min-height: 100vh;
  background: #1a1a2e;
  color: #e0e0e0;
  font-family: Arial, sans-serif;
}

.banner {
  width: 100%;
  max-height: 220px;
  object-fit: cover;
  display: block;
}

/* Top bar */
.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 48px;
  background: #16213e;
  border-bottom: 1px solid #0f3460;
  flex-wrap: wrap;
  gap: 12px;
}

.user-greeting {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.1rem;
}

.pro-badge {
  font-size: 0.75rem;
  font-weight: bold;
  padding: 3px 10px;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.pro-badge--pro {
  background: #1a4a80;
  color: #fff;
}
.pro-badge--free {
  background: #0f3460;
  color: #aaa;
}

.top-bar-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

/* Grid */
.home-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 24px;
  padding: 32px 48px;
  max-width: 1200px;
  margin: 0 auto;
}

.home-card {
  background: #16213e;
  border: 1px solid #0f3460;
  border-radius: 10px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.card-title {
  font-size: 1.1rem;
  font-weight: bold;
  color: #ffffff;
  margin: 0 0 4px;
  padding-bottom: 10px;
  border-bottom: 1px solid #0f3460;
}

/* Tool groups */
.tool-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.group-toggle {
  background: #0f3460;
  color: #e0e0e0;
  width: 100%;
  box-sizing: border-box;
  border: none;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 0.95rem;
  cursor: pointer;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.2s;
}
.group-toggle:hover {
  background: #1a4a80;
}
.chevron {
  font-size: 0.7rem;
  opacity: 0.7;
}

.group-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 12px;
}

.pending-tasks-notice {
  background: rgba(255, 180, 0, 0.08);
  border: 1px solid rgba(255, 180, 0, 0.35);
  border-radius: 6px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pending-tasks-title {
  font-size: 0.82rem;
  font-weight: bold;
  color: #f5c542;
}

.pending-task-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.pending-task-date {
  font-size: 0.78rem;
  color: #9ab8d0;
}

.btn--resume {
  font-size: 0.78rem;
  padding: 3px 10px;
}

.tool-hint {
  font-size: 0.82rem;
  color: #7a9ab8;
  margin: 0 0 4px;
  line-height: 1.5;
}

.creator-learn-more {
  display: block;
  font-size: 0.8rem;
  color: #5dade2;
  text-decoration: none;
  margin: -6px 0 10px;
  opacity: 0.8;
}
.creator-learn-more:hover {
  opacity: 1;
  text-decoration: underline;
}

/* Buttons */
.btn {
  border: none;
  border-radius: 6px;
  padding: 10px 18px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: opacity 0.2s, background 0.2s;
  width: 100%;
  box-sizing: border-box;
}
.btn:hover { opacity: 0.85; }
.btn:disabled { opacity: 0.4; cursor: default; }

.btn--primary {
  background: #1565c0;
  color: #fff;
  font-weight: bold;
  text-align: left;
}
.btn--secondary {
  background: #0f3460;
  color: #ccc;
}
.btn--upgrade {
  background: #f5a623;
  color: #1a1a2e;
  font-weight: bold;
}
.btn--tool {
  background: #0a2a50;
  color: #e0e0e0;
  text-align: left;
}
.btn--tool:hover {
  background: #1a4a80;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal-box {
  background: #16213e;
  border: 1px solid #0f3460;
  border-radius: 10px;
  width: 100%;
  max-width: 860px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-box--slim {
  max-width: 480px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #0f3460;
  font-size: 1rem;
  font-weight: bold;
  color: #ffffff;
  flex-shrink: 0;
}

.modal-close {
  background: none;
  border: none;
  color: #aaa;
  font-size: 1.2rem;
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;
}
.modal-close:hover { color: #fff; }

.modal-body {
  overflow-y: auto;
  padding: 20px;
  flex: 1;
  color: #e0e0e0;
}</style>
